import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CampaignFunnel } from '../../types/accounts';

/** Per-campaign email stats as a scannable table (mirrors the Slack GTM digest granularity).
 *  Columns: Campaign · Sent · Open% · Reply · Opp · Mtg · 24h. Row expands to the full funnel. */

const pct = (n = 0, d = 0) => (d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : '—');
const v = (c: CampaignFunnel, k: string) => c.latest?.[k] ?? 0;

const EXTRA: [string, string][] = [
  ['total_interested', 'Interested'],
  ['total_meeting_completed', 'Mtg completed'],
  ['total_opportunity_value', 'Opp value'],
  ['bounced_count', 'Bounced'],
  ['unsubscribed_count', 'Unsub'],
  ['leads_count', 'Leads'],
];

function CampaignRow({ c }: { c: CampaignFunnel }) {
  const [open, setOpen] = useState(false);
  const d24 = c.delta_24h?.emails_sent_count ?? 0;
  return (
    <>
      <tr
        className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <td className="px-3 py-2">
          <span className="inline-flex items-center gap-1 font-medium text-neutral-900">
            {open ? (
              <ChevronDown size={13} className="text-neutral-400 shrink-0" />
            ) : (
              <ChevronRight size={13} className="text-neutral-400 shrink-0" />
            )}
            <span className="truncate max-w-[16rem]">{c.name ?? c.id}</span>
          </span>
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-neutral-700">{v(c, 'emails_sent_count')}</td>
        <td className="px-3 py-2 text-right tabular-nums text-neutral-700">
          {pct(v(c, 'open_count_unique'), v(c, 'emails_sent_count'))}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-neutral-700">{v(c, 'reply_count_unique')}</td>
        <td className="px-3 py-2 text-right tabular-nums text-neutral-700">{v(c, 'total_opportunities')}</td>
        <td className="px-3 py-2 text-right tabular-nums text-neutral-700">{v(c, 'total_meeting_booked')}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {d24 ? (
            <span className={d24 > 0 ? 'text-success-600' : 'text-error-600'}>
              {d24 > 0 ? '+' : ''}
              {d24}
            </span>
          ) : (
            <span className="text-neutral-300">—</span>
          )}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-neutral-100 bg-neutral-50/60">
          <td colSpan={7} className="px-3 py-2.5">
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {EXTRA.map(([k, label]) => (
                <div key={k} className="text-xs">
                  <span className="text-neutral-400">{label}: </span>
                  <span className="font-medium tabular-nums text-neutral-800">{v(c, k)}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function CampaignStatsTable({ campaigns }: { campaigns: CampaignFunnel[] }) {
  if (!campaigns.length) return null;
  return (
    <div className="overflow-x-auto rounded-md border border-neutral-200">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-neutral-50 border-b border-neutral-100 text-2xs uppercase tracking-wider text-neutral-400">
            <th className="px-3 py-2 text-left font-medium">Campaign</th>
            <th className="px-3 py-2 text-right font-medium">Sent</th>
            <th className="px-3 py-2 text-right font-medium">Open%</th>
            <th className="px-3 py-2 text-right font-medium">Reply</th>
            <th className="px-3 py-2 text-right font-medium">Opp</th>
            <th className="px-3 py-2 text-right font-medium">Mtg</th>
            <th className="px-3 py-2 text-right font-medium">24h</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => (
            <CampaignRow key={c.id} c={c} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
